import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { colors, Divider, Grid, InputAdornment, MenuItem, Radio, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {editProfile, removeViolations} from 'actions';
import {dateTimeHuman, valueByInputTypeTransformer, hasAccess} from 'utils';
import {useDispatch, useSelector} from 'react-redux';
import { userRoles } from '../../constants';
import clsx from 'clsx';
import moment from 'moment';
import { makeStyles } from '@material-ui/styles';
import { KeyboardDatePicker } from '@material-ui/pickers';
import validate from 'validate.js';
import LocationPicker from '../LocationPicker';
import { CITY } from 'utils/Location/locationOptions';
import { middleNameRule } from 'utils/Profile/middleNameRule';
import EmailVerifiedMark from '../EmailVerifiedMark';
import {DATE_FORMAT} from '../../constants';

const MIN_AGE = 3;
const MAX_AGE = 100;

const useStyles = makeStyles(theme => ({
    root: {
    },
    optionRadio: {
        margin: -10
    },
    radioBlockTitle: {
        marginTop: '2px',
    },
    radioBlock: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
    },
    dateField: {
        '& + &': {
            marginLeft: theme.spacing(2)
        }
    },
    optionDetails: {
        marginTop: '1px',
        marginLeft: theme.spacing(2)
    },
    option: {
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'row',
        padding: theme.spacing(2, 1),
        maxWidth: 560,
    },
    selectedOption: {
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: colors.grey[50]
    },
}));

const schema = {
    firstName: {
        presence: { allowEmpty: false, message: 'is required' },
        length: {
            minimum: 3,
            maximum: 100,
        }
    },
    lastName: {
        presence: { allowEmpty: false, message: 'is required' },
        length: {
            minimum: 3,
            maximum: 50,
        }
    },
    middleName: middleNameRule,
    // THE NAME IN LATIN (15 Oct 2026, international competitions): optional —
    // empty means the official transliteration, which the server shows below.
    latinLastName: {
        presence: false,
        format: { pattern: '[^\\u0400-\\u04FF]*', message: '^component.profile.latinOnly' },
        length: { maximum: 100 },
    },
    latinFirstName: {
        presence: false,
        format: { pattern: '[^\\u0400-\\u04FF]*', message: '^component.profile.latinOnly' },
        length: { maximum: 50 },
    },
    weight: {
        presence: false,
        numericality: {
            noStrings: true,
            greaterThan: 0,
            lessThanOrEqualTo: 150,
        },
    },
    birthday: {
        presence: { allowEmpty: false, message: 'is required' },
        datetime: {
            latest: moment().subtract(MIN_AGE, 'years'),
            earliest: moment().subtract(MAX_AGE, 'years'),
        },
    },
    email: {
        presence: false,
        email: {
            message: 'doesn\'t look like a valid email',
        },
    },
    phone: {
        presence: false,
        format: {
            pattern: /^\+?([0-9]{3})\)?[-. ]?([0-9]{2})[-. ]?([0-9]{3})[-. ]?([0-9]{2})[-. ]?([0-9]{2})$/,
            message: 'doesn\'t look like a valid phone. (+380-XX-XXX-XX-XX) ',
        },
        length: {
            minimum: 10,
            maximum: 25,
        }
    },

};

validate.extend(validate.validators.datetime, {
    parse: value => moment(value),
    format: value => moment(value).format(DATE_FORMAT),
});

const Profile = props => {
    const {profile, clubs} = props;
    const initialState = {
        isValid: false,
        values: {
            ...(profile ? {} : {sex: 'male'}),
        },
        touched: {},
        errors: {}
    }
    const classes = useStyles();
    const [valueState, setValueState] = useState({...initialState});
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.userSession.user);
    const canEditBirthday = !profile || hasAccess(currentUser?.roles || [], [userRoles.ADMIN, userRoles.SUPER_ADMIN]);
    // Sex decides the categories too: an administrator's, like the birthday
    // (1 Nov 2026 — it was offered to a coach and silently not saved). The
    // weight stays open here: a coach sets their athletes' (the server
    // refuses anybody else out loud, ProfileAdminFieldGuard).
    const canEditSex = canEditBirthday;
    const violations = useSelector(store => store.validation.violations || []);
    const violationErrors =  violations.reduce((a,b) => {
        const property = b.propertyPath.split('.').pop();
        a[property] = [b.message];
        return a
    }, {});
    const [t] = useTranslation();

    const hasErrors = field =>
        violationErrors[field] || (valueState.touched[field] && valueState.errors[field]) ? true : false;

    const valueError = {...valueState.errors, ...violationErrors};

    const getValue = key => {
        const { values, touched } = valueState;
        if (! profile) {
            return values[key];
        }

        return touched[key] ? values[key] : profile[key];
    }

    const getSchema = () => {
        if (!profile) {
            return schema;
        }
        const { touched } = valueState;

        return Object.keys(touched).reduce(function (changedSchema, key) {
            changedSchema[key] = schema[key];
            return changedSchema;
        }, {});
    }

    useEffect(() => {
        const errors = validate(valueState.values, getSchema());
        setValueState(valueState => ({
            ...valueState,
            isValid: errors ? false : true,
            errors: errors || {}
        }));
        handleProfileChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueState.values]);

    useEffect(() => {
        handleProfileChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueState.isValid]);

    const handleProfileChange = () => {
        const {touched, values, isValid} = valueState;
        if (Object.keys(touched).length === 0) {
            return;
        }
        dispatch(editProfile(values, isValid));
    }

    const handleChange = event => {
        event.persist && event.persist();
        const { type, name, value, checked } = event.target;
        dispatch(removeViolations());
        setNewValue(name, valueByInputTypeTransformer(type, value, checked));
    };

    const setNewValue = (name, value) => {
        setValueState(valueState => ({
            ...valueState,
            values: {
                ...valueState.values,
                [name]: value
            },
            touched: {
                ...valueState.touched,
                [name]: true
            }
        }));
    }

    const handleClubSelect = (e) => {
        e.persist();
        handleChange(e);
        const { values } = valueState;
        if (values.location) {
            return ;
        }
        const { value } = e.target;
        const club = clubs.find(c => c.id === value);

        if (!club) {
            return ;
        }

        club.location && club.location.id && setNewValue('location', club.location.id);
    }

    const getDateTimeFormValue = key => {
        const value = getValue(key);

        if (typeof value === 'string' || value instanceof String) {
            return dateTimeHuman(value, true);
        }

        return value;
    }

    const handleDateChange = name => data => {
        dispatch(removeViolations());
        setNewValue(name, valueByInputTypeTransformer('date', data, false));
    };

    const getTextField = (name, type, isRequired = false, InputProps={}) => {
        return (<TextField
            error={hasErrors(name)}
            fullWidth
            required={isRequired}
            helperText={hasErrors(name) ? valueError[name][0] : null}
            label={t('component.profile.' + name)}
            name={name}
            onChange={handleChange}
            value={getValue(name) || ''}
            variant='outlined'
            type={type}
            InputProps={InputProps}
        />)
    }

    return (<React.Fragment>
        <Grid item md={4} xs={12}>
            {getTextField('lastName', 'text', true)}
        </Grid>

        <Grid item md={4} xs={12}>
            {getTextField('firstName', 'text', true)}
        </Grid>

        <Grid item md={4} xs={12}>
            {getTextField('middleName', 'text')}
        </Grid>

        {/* THE NAME IN LATIN — for international competitions. Empty is
            the normal state: the server's official transliteration is used,
            and shown here so it can be checked (15 Oct 2026). */}
        {['latinLastName', 'latinFirstName'].map(name => (
            <Grid item md={6} xs={12} key={name}>
                <TextField
                    error={hasErrors(name)}
                    fullWidth
                    helperText={hasErrors(name)
                        ? t(valueError[name][0])
                        : (((profile && profile.latinName) || {})['latinLastName' === name ? 'lastName' : 'firstName']
                            ? t('component.profile.latinAutomatic', {
                                value: profile.latinName['latinLastName' === name ? 'lastName' : 'firstName'],
                                defaultValue: 'Empty — {{value}} (official transliteration)',
                            })
                            // A new profile has no name yet to transliterate.
                            : t('component.profile.latinAutomaticNew', 'Empty — the official transliteration'))}
                    label={t('component.profile.' + name)}
                    name={name}
                    onChange={handleChange}
                    value={getValue(name) || ''}
                    variant='outlined'
                    inputProps={{ 'data-testid': 'profile-' + name }}
                />
            </Grid>
        ))}

        <Grid item md={6} xs={12} >
            <div className={classes.radioBlock} >
                <div className={clsx(classes.option)} >
                    <Typography
                        noWrap
                        variant='h5'
                        className={classes.radioBlockTitle}
                    >
                        {t('component.profile.sex')}:
                    </Typography>
                </div>
                <div
                    className={clsx(classes.option, {
                        [classes.selectedOption]: getValue('sex') === 'male'
                    })}
                >
                    <Radio
                        checked={getValue('sex') === 'male'}
                        className={classes.optionRadio}
                        color='primary'
                        disabled={!canEditSex}
                        onClick={canEditSex ? handleChange : undefined}
                        name='sex'
                        value='male'
                    />
                    <div className={classes.optionDetails}>
                        <Typography noWrap variant='h6'>
                            {t('component.profile.male')}
                        </Typography>
                    </div>
                </div>
                <div
                    className={clsx(classes.option, {
                        [classes.selectedOption]: getValue('sex') === 'female'
                    })}
                >
                    <Radio
                        checked={getValue('sex') === 'female'}
                        className={classes.optionRadio}
                        color='primary'
                        name='sex'
                        value='female'
                        disabled={!canEditSex}
                        onClick={canEditSex ? event => handleChange(event) : undefined}
                    />
                    <div className={classes.optionDetails}>
                        <Typography noWrap variant='h6'>
                            {t('component.profile.female')}
                        </Typography>
                    </div>
                </div>
            </div>
            {!canEditSex && (
                <Typography variant='caption' color='textSecondary' data-testid='profile-sex-admin-only'>
                    {t('component.profile.sexAdminOnly')}
                </Typography>
            )}
        </Grid>
        <Grid item md={6} xs={12}>
            <KeyboardDatePicker
                label={t('component.profile.birthday')}
                variant='outlined'
                minDate={moment().subtract(MAX_AGE,'year').format(DATE_FORMAT)}
                maxDate={moment().subtract(MIN_AGE,'year').format(DATE_FORMAT)}
                error={hasErrors('birthday')}
                margin='normal'
                inputVariant='outlined'
                required
                fullWidth
                disabled={!canEditBirthday}
                helperText={
                    !canEditBirthday
                        ? t('component.profile.birthdayAdminOnly')
                        : (hasErrors('birthday') ? valueError.birthday[0] : null)
                }
                format={DATE_FORMAT}
                value={getDateTimeFormValue('birthday')}
                onChange={canEditBirthday ? handleDateChange('birthday') : undefined}
            />
        </Grid>
        <Grid item md={6} xs={12}>
            {getTextField('weight', 'number', false, { inputProps: { min: 10, max: 150, step: 'any' }})}
        </Grid>
        <Grid item md={6} xs={12}>
            {/* The mark is the SAVED address's: typing another one hides it,
                since the new address is proved by nobody yet. */}
            {getTextField('email', 'email', false, profile && profile.emailVerified && profile.email
                && 0 === String(getValue('email') || '').trim().toLowerCase().localeCompare(profile.email.toLowerCase())
                ? { endAdornment: (
                    <InputAdornment position="end">
                        <EmailVerifiedMark verifiedAt={profile.emailVerifiedAt} />
                    </InputAdornment>
                ) }
                : {})}
        </Grid>
        <Grid item md={6} xs={12}>
            {getTextField('phone', 'phone' )}
        </Grid>
        <Grid item md={6} xs={12}>
            <TextField
                error={hasErrors('club')}
                fullWidth
                helperText={hasErrors('club') ? valueError.club[0] : null}
                label={t('component.profile.club')}
                name='club'
                onChange={handleClubSelect}
                select
                // eslint-disable-next-line react/jsx-sort-props
                value={getValue('club') || ''}
                variant='outlined'
                type='text'
            >
                <MenuItem
                    disabled
                    value=''
                >{t('component.profile.selectClub')}</MenuItem>
                <MenuItem
                    value=''
                >{t('component.profile.noClub')}</MenuItem>
                <Divider />
                {clubs.sort((a, b) => a.title < b.title ? -1 : 1).map(club => (
                    <MenuItem
                        key={club.id}
                        value={club.id}
                    >
                        {club.title}
                    </MenuItem>
                ))}
            </TextField>
        </Grid>
        <Grid item md={6} xs={12}>
            {/* An athlete lives in a CITY (organiser, 18 Oct 2026); not required. */}
            <LocationPicker
                kinds={[CITY]}
                allowAdd
                label={t('component.profile.location')}
                value={getValue('location') || null}
                onChange={id => {
                    dispatch(removeViolations());
                    setNewValue('location', id);
                }}
                error={hasErrors('location')}
                helperText={hasErrors('location') ? valueError.location[0] : null}
                testId="profile-location"
            />
        </Grid>
    </React.Fragment>)
}

Profile.propTypes = {
    className: PropTypes.string,
    clubs: PropTypes.array.isRequired,
    locations: PropTypes.array.isRequired,
    profile: PropTypes.object,
}

Profile.defaultProps = {
    clubs: [],
    locations: [],
    profile: null,
}


export default Profile;
